<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div
    class="editor d-flex flex-column flex-grow-1"
    :class="{ 'editor--disabled': usePlaceholder }"
  >
    <div>
      <v-alert v-if="showSaveWorkMessage" id="saveWorkInfoMessage" class="mb-4" type="info">
        {{ MESSAGES.INFO.SAVE_WORK }}
      </v-alert>
    </div>
    <p v-if="usePlaceholder !== null && !richTextEditorModal" class="mb-0">Value</p>
    <TipTapEditor
      ref="tipTapEditor"
      :is-editable="isEditable"
      :support-context-key="supportContextKey"
      :support-image="supportImage"
      :image-layouts="imageLayouts"
      :use-placeholder="usePlaceholder"
      :height="height"
      :model-value="modelValue"
      :has-toolbar="hasToolbar"
      :has-bubble-menu="hasBubbleMenu"
      :has-floating-menu="hasFloatingMenu"
      :allow-print-preview="allowPrintPreview"
      @show-link-editor-dialog="showLinkEditorDialog"
      @remove-link-mark-from-selection="removeLinkMarkFromSelection"
      @add-image="addImage"
      @show-context-keys="showContextKeys"
      @insert-placeholder-text="insertPlaceholderText"
      @handle-paste-content="handlePasteContent"
      @update:model-value="updateModelValue"
    />
    <tip-tap-image-upload-dialog
      ref="uploadDialog"
      :show-dialog="showImageUploadDialog"
      :token="requestToken"
      @closed="showImageUploadDialog = false"
      @on-upload="onImageUpload"
    />
    <tip-tap-context-keys-dialog
      ref="contextKeyDialog"
      :show-dialog="contextKeyDialog"
      @closed="contextKeyDialog = false"
      @insert-context-key="insertContextKey"
    />
    <tip-tap-link-editor-dialog
      :show-dialog="tipTapLinkEditorIsOpen"
      :get-editor="getEditor"
      @hide-link-editor-dialog="hideLinkEditorDialog"
      @remove-link-mark-from-selection="removeLinkMarkFromSelection"
    />
  </div>
</template>

<script>
import { unescape } from 'html-escaper';
import { mapGetters, mapState } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import { MESSAGES, ATTRIBUTES } from '@/constants';
import { content } from '@/api';
import * as types from '@/store/mutationTypes';
import {
  collectingImagesFromContent,
  collectingDataHashFromImage,
  collectingDataSrcFromImage
} from '@/lib/strings';
import updateTableColumnsWidth from '@/lib/tableUtils';
import TipTapEditor from '@/components/content/tiptap/TipTapEditor';
import { IMAGE_LAYOUTS } from '@/components/content/tiptap/imageWithLayout';
import TipTapContextKeysDialog from './TipTapContextKeysDialog';
import TipTapImageUploadDialog from './TipTapImageUploadDialog';
import TipTapLinkEditorDialog from './TipTapLinkEditorDialog';

export default {
  name: 'CEContentEditorTipTap',

  components: {
    TipTapEditor,
    TipTapImageUploadDialog,
    TipTapContextKeysDialog,
    TipTapLinkEditorDialog
  },

  extends: CSBase,

  props: {
    richTextEditorModal: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: String,
      default: ''
    },

    showDialog: {
      type: Boolean,
      default: false
    },

    showSaveWorkMessage: {
      type: Boolean,
      required: false
    },

    supportImage: {
      types: Boolean,
      required: false,
      default: false
    },

    imageLayouts: {
      type: Array,
      required: false,
      default: () => IMAGE_LAYOUTS
    },

    supportContextKey: {
      types: Boolean,
      required: false,
      default: false
    },

    options: {
      type: Object,
      required: false,
      default: () => ({})
    },
    usePlaceholder: {
      type: Boolean,
      default: null
    },
    placeholderText: {
      type: String,
      required: false,
      default: ''
    },
    height: {
      type: String,
      default: '20rem'
    },
    allowPrintPreview: {
      type: Boolean,
      default: false
    },
    hasToolbar: {
      type: Boolean,
      default: false
    },
    hasBubbleMenu: {
      type: Boolean,
      default: false
    },
    hasFloatingMenu: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      contextKeyDialog: false,
      tipTapLinkEditorIsOpen: false,
      MESSAGES,
      showImageUploadDialog: false,
      requestToken: '',
      originalModelValue: ''
    };
  },

  watch: {
    usePlaceholder(newValue, oldValue) {
      const editorInstance = this.getEditor();
      if (!editorInstance) return;

      let contentToSet;

      // Handle transitions explicitly
      if (newValue && !oldValue) {
        // Entering placeholder mode: store original and show placeholder
        this.originalModelValue = this.modelValue;
        contentToSet = this.placeholderText;
      } else if (!newValue && oldValue) {
        // Exiting placeholder mode: restore original
        contentToSet = this.originalModelValue || this.modelValue;
        this.originalModelValue = ''; // Cleanup
      } else {
        // Initial render or no transition: set based on current state
        contentToSet = newValue ? this.placeholderText : this.modelValue;
      }

      // If the modelValue contains images, process it and set it to the editor
      if (collectingImagesFromContent(contentToSet)) {
        this.getImagesFromContent(contentToSet);
      }
      editorInstance.commands.setContent(contentToSet);
    },
    showDialog() {
      // Every time the dialog is open, write the DOM with the content from parent.
      if (this.showDialog) {
        const editorInstance = this.getEditor();
        if (editorInstance) {
          this.getImagesFromContent();
          const contentToDisplay = this.usePlaceholder ? this.placeholderText : this.modelValue;
          editorInstance.commands.setContent(contentToDisplay);
        }
      }
    },

    isLoadingImages(value) {
      const editorInstance = this.getEditor();
      if (!editorInstance) return;

      if (value) {
        editorInstance.setOptions({ editable: false });
      } else {
        editorInstance.setOptions({ editable: true });
      }
    }
  },

  mounted() {
    this.getImagesFromContent();
    this.applyStyledForErrorText();
  },

  computed: {
    ...mapGetters({
      getImageFromImageData: 'imageUploader/getImageFromImageData',
      getUploadedImages: 'imageUploader/getUploadedImages',
      isHubEnabled: 'hub/isEnabled'
    }),

    ...mapState({
      isLoadingImages: (state) => state.appearance.isLoadingImages
    }),
    isEditable() {
      return !this.usePlaceholder;
    }
  },

  methods: {
    getEditor() {
      return this.$refs.tipTapEditor?.getEditor();
    },
    updateModelValue(value) {
      this.$emit('update:modelValue', value);
    },
    setImagesSrc(images, modelValue = this.modelValue, insertPlaceholderText = false) {
      const parser = new DOMParser();
      const parsedHtml = parser.parseFromString(modelValue, 'text/html');
      const imageElements = parsedHtml.getElementsByTagName('img');

      Array.prototype.forEach.call(imageElements, (imageElement, index) => {
        const dataHash = imageElement.getAttribute(ATTRIBUTES.DATA_FILENAME_HASH);

        const dataSRC = images.find((imageSrc) => imageSrc.hash === dataHash);

        if (dataSRC) {
          imageElements[index].alt = dataHash;
          imageElements[index].src = dataSRC.data;
        }
      });
      // only emit event when context key value is provided by user upon saving
      if (modelValue === this.modelValue) {
        this.$emit('update:modelValue', this.updateHTML(parsedHtml.body.innerHTML));
      }

      const editorInstance = this.getEditor();
      if (!editorInstance) {
        console.warn('Editor instance not available yet in setImagesSrc');
        return;
      }
      if (insertPlaceholderText) {
        editorInstance.chain().focus().insertContent(parsedHtml.body.innerHTML).run();
      } else {
        editorInstance.commands.setContent(this.updateHTML(parsedHtml.body.innerHTML));
      }
    },

    async getImagesFromContent(modelValue = this.modelValue, insertPlaceholderText = false) {
      const imagesInValue = collectingImagesFromContent(modelValue);
      const imagePromises = [];

      if (imagesInValue && imagesInValue.length) {
        imagesInValue.forEach((image) => {
          const dataHash = collectingDataHashFromImage(image);
          const src = collectingDataSrcFromImage(image);

          if (!src) {
            imagePromises.push(content.getImage(dataHash));
          }
        });
      }

      if (imagePromises.length) {
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, true);

        const responses = await Promise.all(
          imagePromises.map((promise) => promise.catch((err) => err))
        );

        const images = responses.map((response) => {
          const hash = response.config.url.split('/images/')[1];
          return {
            data: response.data,
            hash
          };
        });
        this.setImagesSrc(images, modelValue, insertPlaceholderText);
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, false);
      }
    },

    async handlePasteContent(pasteContent) {
      const imagesInValue = collectingImagesFromContent(pasteContent);
      const imagePromises = [];
      const parser = new DOMParser();

      const editorInstance = this.getEditor();
      this.$nextTick(() => {
        const parsedHtml = parser.parseFromString(editorInstance.getHTML(), 'text/html');

        const regexFixHtml = this.updateHTML(parsedHtml.body.innerHTML);

        const updateTableColmunsWidth = updateTableColumnsWidth(regexFixHtml);

        editorInstance.commands.setContent(this.updateHTML(updateTableColmunsWidth));
      });

      if (imagesInValue && imagesInValue.length) {
        imagesInValue.forEach((image) => {
          const dataHash = collectingDataHashFromImage(image);
          const src = collectingDataSrcFromImage(image);

          if (!src) {
            imagePromises.push(content.getImage(dataHash));
          }
        });
      }

      if (imagePromises.length) {
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, true);

        const responses = await Promise.all(
          imagePromises.map((promise) => promise.catch((err) => err))
        );

        const images = responses.map((response) => {
          const hash = response.config.url.split('/images/')[1];
          return {
            data: response.data,
            hash
          };
        });
        this.setImagesSrc(images);
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, false);
      }
    },

    isEncodedUri(uri) {
      // eslint-disable-next-line prefer-regex-literals
      const uriRegex = new RegExp(/%[0-9a-f]{2}/i);
      return uri ? uriRegex.test(uri) : true;
    },

    updateHTML(html) {
      // Update links in HTML.
      const regex = /href\s*=\s*(['"])(https?:\/\/.+?)\1/gi;
      let unescapedHtml = unescape(html);
      const links = [...unescapedHtml.matchAll(regex)];

      links.forEach((link) => {
        const encodedUri = this.isEncodedUri(link[2]) ? link[2] : encodeURI(link[2]);
        const uri = encodedUri.replace(/\(/g, '%28').replace(/\)/g, '%29');
        unescapedHtml = unescapedHtml.replace(link[2], uri);
      });

      // Update line breaks in HTML.
      unescapedHtml = unescapedHtml.replace(/<p><\/p>/g, '<p><br></p>');

      return unescapedHtml;
    },

    async addImage() {
      this.requestToken = await this.$store.dispatch(
        this.isHubEnabled ? 'hub-session/getToken' : 'cs-session/getToken'
      );

      this.showImageUploadDialog = true;
    },

    showContextKeys() {
      this.contextKeyDialog = true;
    },

    insertContextKey(contextKey) {
      const editorInstance = this.getEditor();
      const transaction = editorInstance.state.tr.insertText(contextKey);
      editorInstance.view.dispatch(transaction);
      this.contextKeyDialog = false;
    },

    async onImageUpload() {
      const images = this.getUploadedImages();

      const imagesContent = images
        .filter((image) => image.file)
        .map((image) => {
          const title = image.file.name.split('.')[0];
          const imageData = {
            type: 'image',
            attrs: {
              src: image.file.dataURL,
              alt: image.hashCode,
              title
            }
          };
          return imageData;
        });

      if (!imagesContent.length) return;

      const editorInstance = this.getEditor();
      editorInstance.chain().focus().insertContent(imagesContent).run();
    },

    showLinkEditorDialog() {
      const numberOfLinksHighlighted = this.checkNumberOfLinksHighlighted();
      if (numberOfLinksHighlighted > 1) {
        // eslint-disable-next-line no-alert
        alert(`You can't edit more than 1 link at a time.`);
        return;
      }
      this.tipTapLinkEditorIsOpen = true;
    },

    hideLinkEditorDialog() {
      this.tipTapLinkEditorIsOpen = false;
    },

    removeLinkMarkFromSelection() {
      const editorInstance = this.getEditor();
      if (!editorInstance) return;
      editorInstance.chain().focus().unsetLink().run();
    },

    checkNumberOfLinksHighlighted() {
      let numberOfLinksHighlighted = 0;

      const editorInstance = this.getEditor();
      if (!editorInstance) return numberOfLinksHighlighted;

      const editorState = editorInstance.state;
      const { from, to } = editorState.selection;
      editorState.doc.nodesBetween(from, to, (node) => {
        const eligibleMark = node.marks?.find((markItem) => markItem?.type?.name === 'link');
        if (eligibleMark) {
          numberOfLinksHighlighted += 1;
        }
      });
      return numberOfLinksHighlighted;
    },

    insertPlaceholderText() {
      const editorInstance = this.getEditor();
      if (this.placeholderText) {
        // If the placeholder text contains images, process placeholderText including images and insert it into the editor
        if (collectingImagesFromContent(this.placeholderText)) {
          this.getImagesFromContent(this.placeholderText, true);
        } else {
          // if the placeholder text does not contain images, insert it directly into the editor
          editorInstance.chain().focus().insertContent(this.placeholderText).run();
        }
      } else {
        console.warn('No placeholder text available');
      }
    },

    async applyStyledForErrorText() {
      const editorInstance = this.getEditor();
      if (editorInstance) {
        // to apply the style for error text
        editorInstance.commands.setContent(this.modelValue);
      }
    }
  }
};
</script>

<style scoped>
.editor__content {
  word-wrap: break-word;
  overflow: auto;
  border: 2px solid #dddddd;
  padding-left: 1rem;
  padding-right: 1rem;
  flex-grow: 1;
  height: 100%;
}
.v-dialog .editor__content {
  height: var(--editor-content-height, 20rem) !important;
}
.editor__content :deep(.tiptap.ProseMirror) {
  min-height: calc(100% - 30px);
}
.editor__content :deep(.ProseMirror:focus) {
  outline: none !important;
}

.editor__content :deep(h1),
.editor__content :deep(h2),
.editor__content :deep(h3),
.editor__content :deep(h4),
.editor__content :deep(h5),
.editor__content :deep(p),
.editor__content :deep(ul),
.editor__content :deep(ol),
.editor__content :deep(pre),
.editor__content :deep(blockquote) {
  margin: 1rem 0;
}

.editor__content :deep(h1),
.editor__content :deep(h2),
.editor__content :deep(h3),
.editor__content :deep(h4),
.editor__content :deep(h5) {
  line-height: 1.3;
}

.editor__content :deep(pre) {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}

.editor__content :deep(pre code) {
  display: block;
}

.editor__content :deep(p code) {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
}

.editor__content :deep(ul),
.editor__content :deep(ol) {
  padding-left: 1rem;
}

.editor__content :deep(li > p),
.editor__content :deep(li > ol),
.editor__content :deep(li > ul) {
  margin: 0;
}

.editor__content :deep(ol ol) {
  list-style-type: lower-alpha;
}

.editor__content :deep(ol ol ol) {
  list-style-type: lower-roman;
}

.editor__content :deep(a) {
  color: rgb(0, 173, 239);
  cursor: pointer;
}

.editor__content :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
  padding-left: 0.8rem;
  font-style: italic;
}

.editor__content :deep(blockquote p) {
  margin: 0;
}

.editor__content :deep(img) {
  display: block;
  margin: 0 auto 1rem auto;
  max-width: 80%;
  width: auto !important;
  height: auto !important;
  border-radius: 3px;
}

.editor__content :deep(table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}

.editor__content :deep(table th) {
  min-width: 1em;
  border: 1px solid #dddddd !important;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.editor__content :deep(table td) {
  min-width: 1em;
  border: 1px solid #dddddd !important;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.editor__content :deep(table td > *),
.editor__content :deep(table th > *) {
  margin-bottom: 0;
}

.editor__content :deep(table th) {
  font-weight: bold;
  text-align: left;
}

.editor__content :deep(table .selectedCell:after) {
  z-index: 2;
  position: absolute;
  content: '';
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgb(0, 173, 239, 0.2);
  pointer-events: none;
}

.editor__content :deep(table .column-resize-handle) {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  z-index: 20;
  background-color: rgb(0, 173, 239, 0.4);
  pointer-events: none;
}

.editor__content :deep(.tableWrapper) {
  margin: 1em 0;
  overflow-x: auto;
}

.editor__content :deep(.resize-cursor) {
  cursor: ew-resize;
  cursor: col-resize;
}

.menubar {
  display: flex;
  flex-wrap: wrap;
  transition:
    visibility 0.2s 0.4s,
    opacity 0.2s 0.4s;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.menubar.is-hidden {
  visibility: hidden;
  opacity: 0;
}

.menubar.is-focused {
  visibility: visible;
  opacity: 1;
  transition:
    visibility 0.2s,
    opacity 0.2s;
}

.menubar__button {
  font-weight: bold;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: rgba(0, 0, 0, 0.54);
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}

.menubar__button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.menubar__button.is-active {
  background-color: rgba(0, 0, 0, 0.1);
}

.menubar__button:focus,
.menububble__input:focus {
  outline: none !important;
}

.menububble {
  display: flex;
  background: rgb(var(--v-theme-black-lighten-5));
  border-radius: 0.3rem;
  padding: 0;
  margin: 0;
  visibility: hidden;
  opacity: 0;
  transition:
    opacity 0.2s,
    visibility 0.2s;
}

.menububble.is-active {
  display: block;
  opacity: 1;
  visibility: visible;
  margin: 0;
}

.menububble__button {
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #fff;
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}

.menububble__button.is-active {
  background-color: hsla(0, 0%, 100%, 0.2);
}

.menububble__button:last-child {
  margin-right: 0;
}

.menububble__form {
  display: flex;
}

.menububble__input {
  font: inherit;
  border: none;
  background: transparent;
  color: #fff;
  width: 100%;
  margin: 0 0.5rem 0 0.5rem;
}

#saveWorkInfoMessage {
  word-break: keep-all;
}

.editor__content :deep(br + br.ProseMirror-trailingBreak) {
  display: none;
  min-height: 100%;
}

.editor--disabled .editor__content :deep(*) {
  pointer-events: none;
}

.editor--disabled .editor__content {
  pointer-events: auto;
  overflow: auto;
}
</style>
